import Button from "devextreme-react/button";
import DateBox from "devextreme-react/date-box";
import React from "react";
import { PhaseAction } from "../../../../../dtos/PhaseAction";
import { PhaseDto } from "../../../../../dtos/PhaseDto";
import { TasklistMode } from "../../context/TaskListContext";
import "./TaskActionsView.css";

type ITaskActionsViewProps = {
    phase: PhaseDto | null;

    dueDateRef: React.RefObject<any>;
    effectiveMinDate: Date;

    hasRelease: boolean;
    hasSubmit: boolean;
    hasRequestRevision: boolean;
    hasApprove: boolean;
    reportButtonVisible: boolean;

    handleActionClick: (message: string | null, action: PhaseAction) => Promise<void>;
    downloadRequestResponseReport: () => Promise<void>;
    onDueDateChanged: (val: Date | null) => void;
    onStartDateChanged?: (val: Date | null) => void;
    onProjectedEndDateChanged?: (val: Date | null) => void;
    editableTaskDates?: boolean;
    mode?: TasklistMode;
};

export function TaskActionsView({
    phase,
    dueDateRef,
    effectiveMinDate,
    hasRelease,
    hasSubmit,
    hasRequestRevision,
    hasApprove,
    reportButtonVisible,
    handleActionClick,
    downloadRequestResponseReport,
    onDueDateChanged,
    onStartDateChanged,
    onProjectedEndDateChanged,
    editableTaskDates = true,
    mode = "default",
}: ITaskActionsViewProps) {
    if (!phase) return null;

    const isTracked = phase?.defaultPhase?.settings?.tracked ?? false;
    const isTemplateMode = mode === "templates";

    return (
        <>
            {hasRelease && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <DateBox
                        displayFormat="MM/dd/yyyy"
                        label="Due Date"
                        min={effectiveMinDate}
                        placeholder="Select a date"
                        ref={dueDateRef}
                        showClearButton={true}
                        style={{ marginTop: 0 }}
                        value={phase.dueDate as Date}
                        width={175}
                        onValueChanged={(e: any) => onDueDateChanged((e?.value as Date) ?? null)}
                    />
                    {isTracked && onStartDateChanged && (
                        editableTaskDates ? (
                            <DateBox
                                displayFormat="MM/dd/yyyy"
                                label="Start Date"
                                placeholder="Select a date"
                                showClearButton={true}
                                style={{ marginTop: 0 }}
                                value={phase.startDate as Date}
                                width={175}
                                onValueChanged={(e: any) => onStartDateChanged((e?.value as Date) ?? null)}
                            />
                        ) : (
                            <div style={{ display: "flex", flexDirection: "column", minWidth: 175 }}>
                                <label style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>Start Date</label>
                                <div style={{ fontSize: "14px" }}>
                                    {phase.startDate ? new Date(phase.startDate).toLocaleDateString() : "Not set"}
                                </div>
                            </div>
                        )
                    )}
                    {isTracked && onProjectedEndDateChanged && (
                        editableTaskDates ? (
                            <DateBox
                                displayFormat="MM/dd/yyyy"
                                label="Projected End Date"
                                placeholder="Select a date"
                                showClearButton={true}
                                style={{ marginTop: 0 }}
                                value={phase.projectedEndDate as Date}
                                width={175}
                                onValueChanged={(e: any) => onProjectedEndDateChanged((e?.value as Date) ?? null)}
                            />
                        ) : (
                            <div style={{ display: "flex", flexDirection: "column", minWidth: 175 }}>
                                <label style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>Projected End Date</label>
                                <div style={{ fontSize: "14px" }}>
                                    {phase.projectedEndDate ? new Date(phase.projectedEndDate).toLocaleDateString() : "Not set"}
                                </div>
                            </div>
                        )
                    )}
                    <Button
                        className="phase-action-button"
                        icon="selectall"
                        hint="Release task"
                        text="Release task"
                        onClick={() =>
                            handleActionClick(
                                `Are you sure you want to mark all subtasks as Ready and Release the ${phase.name}?`,
                                PhaseAction.RELEASE
                            )
                        }
                    />
                </div>
            )}

            {hasSubmit && (
                <Button
                    className="phase-action-button"
                    icon="upload"
                    hint="Submit task"
                    text="Submit Task"
                    onClick={() => handleActionClick("Are you sure you want to submit this task?", PhaseAction.SUBMIT)}
                />
            )}

            {hasRequestRevision && (
                <Button
                    className="phase-action-button"
                    icon="parentfolder"
                    hint="Request revisions"
                    text="Request revisions"
                    onClick={() =>
                        handleActionClick(
                            `Are you sure you want to request revisions for ${phase.name}?`,
                            PhaseAction.REQUESTREVISION
                        )
                    }
                />
            )}

            {hasApprove && (
                <Button
                    className="phase-action-button"
                    icon="parentfolder"
                    hint="Approve task"
                    text="Approve task"
                    onClick={() =>
                        handleActionClick(
                            `Are you sure you want to set all subtasks as Approved and Approve the ${phase.name}?`,
                            PhaseAction.APPROVE
                        )
                    }
                />
            )}

            {reportButtonVisible && (
                <Button
                    className="phase-action-button"
                    icon="download"
                    hint="Generate Report"
                    text="Generate Report"
                    onClick={downloadRequestResponseReport}
                />
            )}

        </>
    );
}
